import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { api } from "@/lib/api";
import { NAV, Sidebar } from "@/components/Sidebar";
import { Overview } from "@/components/views/Overview";
import { Registro } from "@/components/views/Registro";
import { ProveedoresView } from "@/components/views/ProveedoresView";
import { GmailInbox } from "@/components/views/GmailInbox";
import { DriveView } from "@/components/views/DriveView";
import { LogView } from "@/components/views/LogView";
import { ConfigView } from "@/components/views/ConfigView";
import { FacturaDrawer } from "@/components/FacturaDrawer";
import { UploadModal } from "@/components/UploadModal";
import { AntigravityCanvas } from "@/components/AntigravityCanvas";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ArrowClockwise, CircleNotch, UploadSimple, List } from "@phosphor-icons/react";


const EMPTY_STATS = {
  total_facturas: 0,
  ok: 0,
  incidencias: 0,
  abonos: 0,
  duplicados: 0,
  emails_total: 0,
  emails_procesados: 0,
  pendientes: 0,
  proveedores_count: 0,
  total_gasto_neto: 0,
  total_iva_soportado: 0,
  desglose_iva: {
    iva_4: { base: 0, cuota: 0 },
    iva_10: { base: 0, cuota: 0 },
    iva_21: { base: 0, cuota: 0 },
  },
  last_polled_at: null,
};

const DEFAULT_CFG = {
  mailbox: "facturacion@cocoand.com",
  poll_min: 5,
  modelo: "gemini-flash-latest",
  mode: "real",
  sheet_name: "Control de Facturas — cocoand",
  drive_root: "Facturas",
};

export default function App() {
  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(EMPTY_STATS);
  const [facturas, setFacturas] = useState([]);
  const [emails, setEmails] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [tree, setTree] = useState({ name: "Facturas", children: {} });
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [gstatus, setGstatus] = useState({ configured: true, connected: false });
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [running, setRunning] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);


  const load = useCallback(async () => {
    try {
      const [s, f, e, provs, t, c, g] = await Promise.all([
        api.stats(),
        api.facturas(),
        api.emails(),
        api.proveedores(),
        api.driveTree(),
        api.config(),
        api.googleStatus(),
      ]);
      setStats(s || EMPTY_STATS);
      setFacturas(Array.isArray(f) ? f : []);
      setEmails(Array.isArray(e) ? e : []);
      setProveedores(Array.isArray(provs) ? provs : []);
      setTree(t || { name: "Facturas", children: {} });
      setCfg(c || DEFAULT_CFG);
      setGstatus(g || { configured: true, connected: false });
    } catch (err) {
      console.warn("Error al cargar datos del backend:", err);
    }
  }, []);

  useEffect(() => {
    document.title = "Facturapp | Dashboard automatizado de facturación by COCO&CO";
    load();
  }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("google");
    if (p === "connected") toast.success("Google Workspace conectado correctamente.");
    else if (p === "error") toast.error("No se pudo completar la conexión con Google.");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const openFactura = (f) => {
    setSelected(f);
    setDrawer(true);
  };

  const handleDeleteFactura = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta factura del sistema?")) return;
    const tid = toast.loading("Eliminando factura...");
    try {
      await api.deleteFactura(id);
      toast.success("Factura eliminada correctamente.", { id: tid });
      if (drawer && selected?.id === id) {
        setDrawer(false);
        setSelected(null);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar la factura.", { id: tid });
    }
  };

  const connectGoogle = async () => {
    try {
      const { auth_url } = await api.googleStart();
      window.location.href = auth_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Configura primero las credenciales OAuth en el servidor.");
    }
  };

  const disconnectGoogle = async () => {
    try {
      await api.googleDisconnect();
      await load();
      toast.success("Google Workspace desconectado.");
    } catch {
      toast.info("Desconectado de Google Workspace.");
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const runPoll = async () => {
    setRunning(true);
    const tid = toast.loading("Procesando buzón… extracción inteligente con Gemini Flash");
    try {
      await api.run();
      let guard = 0;
      let last = null;
      while (guard < 40) {
        await sleep(2500);
        const st = await api.status();
        await load();
        last = st.last;
        if (!st.running) break;
        guard += 1;
      }
      const n = last?.procesados ?? 0;
      if (last?.aviso) {
        toast.warning(last.aviso, { id: tid, duration: 9000 });
      } else if (last?.error) {
        toast.error(`Aviso en pipeline: ${last.error}`, { id: tid });
      } else {
        toast.success(`Polling completado · ${n} email(s) procesados`, { id: tid });
      }
    } catch (err) {
      toast.error("Error al iniciar polling.", { id: tid });
    } finally {
      setRunning(false);
      await load();
    }
  };

  return (
    <div className="relative flex min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Capa de fondo Antigravity 2.5.5 optimizada */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-40">
        <AntigravityCanvas variant="subtle" interactive={false} />
      </div>

      <Toaster position="top-right" richColors />
      <Sidebar
        active={active}
        setActive={setActive}
        cfg={cfg}
        gstatus={gstatus}
        mobileOpen={mobileMenuOpen}
        setMobileOpen={setMobileMenuOpen}
      />

      <main className="relative z-10 flex-1 min-w-0 flex flex-col">
        {/* Header Responsivo */}
        <header className="sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6 md:px-8 py-3.5 bg-white/85 backdrop-blur-md border-b border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-1 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Abrir menú de navegación"
            >
              <List size={22} weight="bold" />
            </button>
            <div className="min-w-0">
              <h1 className="font-display text-lg sm:text-xl md:text-2xl font-bold tracking-tight text-slate-900 truncate">
                {NAV.find((n) => n.id === active)?.label}
              </h1>
              <p className="text-[11px] sm:text-xs text-slate-400 truncate hidden sm:block">
                Pipeline headless · Gmail <code>facturacion@cocoand.com</code> → Gemini Flash → Drive → Sheets
              </p>
            </div>
          </div>

          {/* Acciones principales adaptativas */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button
              data-testid="btn-upload-factura"
              onClick={() => setUploadModalOpen(true)}
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs sm:text-sm border-slate-300 text-slate-700 bg-white hover:bg-slate-50 hover:text-slate-900 shadow-sm"
            >
              <UploadSimple size={16} weight="bold" />
              <span className="hidden sm:inline">Subir Factura</span>
              <span className="sm:hidden">Subir</span>
            </Button>
            <Button
              data-testid="btn-run-polling"
              onClick={runPoll}
              disabled={running}
              size="sm"
              className="gap-1.5 text-xs sm:text-sm bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
            >
              {running ? <CircleNotch size={16} className="animate-spin" /> : <ArrowClockwise size={16} />}
              <span className="hidden sm:inline">Ejecutar polling ahora</span>
              <span className="sm:hidden">Polling</span>
            </Button>
          </div>
        </header>

        {/* Contenedor Principal */}
        <div className="flex-1 p-4 sm:p-6 md:p-8 max-w-7xl w-full mx-auto">
          {active === "overview" && (
            <Overview
              stats={stats}
              facturas={facturas}
              onOpen={openFactura}
              onDelete={handleDeleteFactura}
              onNavigate={setActive}
            />
          )}

          {active === "registro" && (
            <Registro
              facturas={facturas}
              onOpen={openFactura}
              onDelete={handleDeleteFactura}
            />
          )}

          {active === "proveedores" && (
            <ProveedoresView
              proveedores={proveedores}
              onRefresh={load}
              onNavigate={setActive}
            />
          )}

          {active === "gmail" && <GmailInbox emails={emails} />}

          {active === "drive" && <DriveView tree={tree} onOpenFactura={openFactura} />}

          {active === "logs" && <LogView />}

          {active === "config" && (
            <ConfigView
              cfg={cfg}
              gstatus={gstatus}
              onConnect={connectGoogle}
              onDisconnect={disconnectGoogle}
            />
          )}
        </div>
      </main>

      <FacturaDrawer
        factura={selected}
        open={drawer}
        onOpenChange={setDrawer}
        onDelete={handleDeleteFactura}
      />

      <UploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={load}
        onOpenFactura={openFactura}
      />
    </div>
  );
}


